import type { OverviewSnapshot } from '@kenocyber/game-domain';
import type { ReactNode } from 'react';
import { DEV_TOOLS_ALLOWED } from '../dev-tools';
import type { HuntSyncStatus } from '../game/useHuntSession';
import { AudioControl } from './AudioControl';
import { formatCountdown } from './format';

interface TopBarProps {
  readonly snapshot: OverviewSnapshot | null;
  readonly fps: number;
  readonly debug: boolean;
  /** Authenticated player's public name, shown discreetly. */
  readonly playerName: string;
  readonly onSignOut: () => void;
  /** Where the authoritative save stands right now. */
  readonly syncStatus: HuntSyncStatus;
  /** False for a debug session: it earns nothing, and the HUD says so. */
  readonly economic: boolean;
  /** Whether the tab row is showing. */
  readonly menuOpen: boolean;
  readonly onToggleMenu: () => void;
  /**
   * The tab row itself, handed in rather than built here.
   *
   * The bar owns WHERE it goes; the app owns what is in it and what each
   * button does. Passing the finished nav keeps the overlay state in one place
   * instead of threading six callbacks through this component.
   */
  readonly tabs: ReactNode;
  /**
   * Whether the chips that describe the HUNT belong on screen.
   *
   * False during a duel. The map, the boss timer, the hunt's state, its sync
   * badge and its frame counter all caption a game that is paused and hidden,
   * and leaving them up over an arena is captioning the wrong thing - a boss
   * counting down beside a duel reads as a boss about to arrive in it.
   *
   * The account chip and the sign-out button stay: they are the shell's, not
   * the hunt's, and a player must always be able to leave.
   */
  readonly huntChipsVisible?: boolean;
}

/**
 * Discreet, honest labels for the save state.
 *
 * `null` means "nothing worth saying" - a steady session should not blink a
 * status chip at the player every 30 seconds.
 */
function syncLabel(status: HuntSyncStatus): { text: string; tone: string } | null {
  switch (status) {
    case 'loading':
      return { text: 'Carregando progresso', tone: '' };
    case 'syncing':
      return { text: 'Sincronizando', tone: '' };
    case 'saved':
      return { text: 'Progresso salvo', tone: 'chip-ok' };
    case 'reconnecting':
      return { text: 'Reconectando', tone: 'chip-danger' };
    case 'conflict':
      return { text: 'Sessão em conflito', tone: 'chip-danger' };
    case 'unauthorized':
      return { text: 'Sessão expirada', tone: 'chip-danger' };
    case 'failed':
      return { text: 'Servidor indisponível', tone: 'chip-danger' };
    case 'suspended':
      // Behind an expedition the hunt saves nothing and asks for nothing, so
      // there is nothing about its save to say - least of all an alarm.
      return null;
    default:
      return null;
  }
}

export function TopBar({
  snapshot,
  fps,
  debug,
  playerName,
  onSignOut,
  syncStatus,
  economic,
  menuOpen,
  onToggleMenu,
  tabs,
  huntChipsVisible = true,
}: TopBarProps) {
  const bossLabel = snapshot
    ? snapshot.bossActive
      ? 'EM CAMPO'
      : formatCountdown(snapshot.bossCountdownSeconds)
    : '—';
  const stateLabel = snapshot ? (snapshot.phase === 'active' ? 'Caçando' : 'Abatida') : 'Iniciando';
  const sync = syncLabel(syncStatus);

  return (
    <header className="top-bar">
      <h1 className="app-title">KENOCYBER</h1>
      {/*
       * The tab row, in the band between the title and the status chips.
       *
       * It used to float over the arena on its own absolute position. Nothing
       * was wrong with that except that it duplicated a layout the bar already
       * performs - and a row hovering a few pixels below a bar reads as a
       * second bar rather than as part of the first.
       */}
      {tabs}
      <div className="top-info">
        {huntChipsVisible ? (
          <>
            <span className="top-chip" data-testid="top-map">
              Mapa {snapshot?.mapIndex ?? 1}
            </span>
            <span
              className={`top-chip ${snapshot?.bossActive ? 'chip-danger' : ''}`}
              data-testid="top-boss"
              title="Tempo até o boss"
            >
              Boss {bossLabel}
            </span>
            <span
              className={`top-chip ${snapshot?.phase === 'dead' ? 'chip-danger' : 'chip-ok'}`}
              data-testid="top-state"
            >
              {stateLabel}
            </span>
            {sync ? (
              <span className={`top-chip top-sync ${sync.tone}`} data-testid="hunt-sync">
                {sync.text}
              </span>
            ) : null}
            {DEV_TOOLS_ALLOWED && debug && !economic ? (
              // A debug session never persists progress. Saying so avoids anyone
              // mistaking a test run for real progression.
              <span className="top-chip chip-dev" data-testid="hunt-non-economic">
                Sessão de teste
              </span>
            ) : null}
            {DEV_TOOLS_ALLOWED && debug ? (
              <span className="top-chip chip-dev" data-testid="top-fps">
                {fps} FPS
              </span>
            ) : null}
          </>
        ) : null}
        {/*
         * O volume, ao lado da conta e do sair (gate V28G.N.1).
         *
         * Fora do bloco das fichas da caça, e é deliberado: o som é da SESSÃO e
         * não da caçada. Um controlo que desaparecesse ao entrar num duelo
         * deixaria o jogador sem maneira de calar exatamente o modo que faz mais
         * barulho.
         *
         * Sem props: ele fala com o mestre do áudio diretamente, porque o que
         * mostra não pertence a esta barra nem a nenhum ecrã - pertence à aba.
         */}
        <AudioControl />
        <span className="top-chip top-player" data-testid="auth-user-name" title="Conta conectada">
          {playerName}
        </span>
        <button
          type="button"
          className="top-chip top-signout"
          data-testid="auth-signout"
          onClick={onSignOut}
        >
          Sair
        </button>
        {/*
         * Last in the bar, hard against the right edge.
         *
         * It stays here whether the row it controls is showing or not: a
         * control that vanished along with the thing it toggles would leave no
         * way back to any of the six.
         */}
        <button
          type="button"
          className={`top-menu-toggle${menuOpen ? ' is-open' : ''}`}
          data-testid="arena-menu-toggle"
          aria-expanded={menuOpen}
          aria-controls="arena-tabs"
          title={menuOpen ? 'Ocultar menu' : 'Mostrar menu'}
          onClick={onToggleMenu}
        >
          <span className="top-menu-glyph" aria-hidden="true" />
          MENU
        </button>
      </div>
    </header>
  );
}
